import React, { useEffect, useMemo, useState } from "react";
import CommonSelect from "../../../core/common/commonSelect";
import { status } from "../../../core/common/selectoption/selectoption";
import { Link, useLocation } from "react-router-dom";
import PredefinedDateRanges from "../../../core/common/datePicker";
import Table from "../../../core/common/dataTable/index";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { assetCategoriesDetails } from "../../../core/data/json/assetcategoryDetails";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { Drawer, message } from "antd";
import { createAssetCategory, deleteAssetCategory, fetchAllAssetCategories, updateAssetCategory, } from "../../../core/data/redux/asset/assetCategoryActions";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import { exportToCSV, exportToExcel, exportToPDF, } from "../../../core/common/GlobalFunctions/exportTableData";
import { decryptData } from "../../../core/utils/encryptUtils";

const AssetsCategory = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const { user } = useSelector((state: any) => state.auth);
  const { assetCategories, loading } = useSelector((state: any) => state.asset.assetCategory);

  const [assetCategory, setAssetCategory] = useState({
    category_name: "",
    status: "",
  });

  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];


  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  const resetForm = () => {
    setAssetCategory({
      category_name: "",
      status: "",
    });
  };

  useEffect(() => {
    if (!assetCategories?.length) {
      dispatch(fetchAllAssetCategories());
    }
  }, [dispatch]);

  const handleInputChange = (e: any, field: string) => {
    const { value } = e.target;
    setAssetCategory((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const showModal = (type: "edit" | "create") => {
    setModalType(type);
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setIsModalVisible(false);
    setModalType("");
    resetForm();
  };
  const handleEditAssetCategory = (record: any): void => {
    setSelectedRowData(record);
    setAssetCategory({
      category_name: record.category_name,
      status: record.status,
    });
    showModal("edit");
  };

  const handleSubmit = async () => {
    if (!assetCategory.category_name.trim()) {
      message.error("Category Name is required");
      return;
    }
    if (!assetCategory.status) {
      message.error("Status is required");
      return;
    }
    const data = {
      ...assetCategory,
      created_by: modalType === "create" ? user.UserId : undefined,
      modified_by: modalType === "edit" ? user.UserId : undefined,
    };
    try {
      let result;
      if (modalType === "create") {
        result = await dispatch(createAssetCategory(data)).unwrap();
      } else if (modalType === "edit") {
        if (selectedRowData) {
          result = await dispatch(
            updateAssetCategory({
              categoryId: selectedRowData.asset_category_id,
              ...data,
            })
          ).unwrap();
        }
      }

      if (result) {
        message.success(result.message);
        await dispatch(fetchAllAssetCategories());
        setTableKey((prevKey) => prevKey + 1);
        resetForm();
      } else {
        alert(result?.payload.error);
      }
      handleCancel();
    } catch (error: any) {
      console.log("error", error);
      if (error?.data?.error === "Holiday with this name already exists") {
        message.error("Category with this name already exists");
      } else {
        message.error("An error occurred");
      }
    }
  };

  const handleDeleteAssetCategory = async (categoryId: number) => {
    try {
      const result = await dispatch(deleteAssetCategory(categoryId)).unwrap();
      if (result) {
        message.success(result.message);
        await dispatch(fetchAllAssetCategories());
        setTableKey((prevKey) => prevKey + 1);
        setIsDeleteModalVisible(false);
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const data = assetCategories ? assetCategories : [];

  const exportAssetCategories = data.map((item: any) => ({
    category_name: item.category_name,
    created_at: item.created_at ? new Date(item.created_at).toLocaleString("en-IN") : "N/A",
    status: item.status,
  }));

  const filteredData = useMemo(() => {
    const filters =
      selectedStatus && selectedStatus !== "All"
        ? { status: selectedStatus }
        : {};
    return filterData(
      data || [],
      filters,
      "created_at",
      "created_at",
      selectedSort
    );
  }, [assetCategories, selectedStatus, selectedSort]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [selectedStatus, selectedSort]);
  const columns = [
    {
      title: "Category Name",
      dataIndex: "category_name",
      render: (text: String) => <h6 className="fs-14 fw-medium">{text}</h6>,
      sorter: (a: any, b: any) =>
        a.category_name.length - b.category_name.length,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string, record: any) => (
        <>
          <span
            className={`badge d-inline-flex align-items-center badge-xs ${text === "active"
              ? "badge-success"
              : text === "inactive"
                ? "badge-danger"
                : "badge-warning"
              }`}
          >
            <i className="ti ti-point-filled me-1"></i>
            {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}

          </span>
        </>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: any) => {
        const isInactive = record.status === "inactive";
        const isEditDisabled = !canEdit;
        const isDeleteDisabled = !canDelete || isInactive;

        return (
          <div className="action-icon d-inline-flex">

            <button
              className="me-2"
              onClick={() => {
                if (!isEditDisabled) {
                  showModal("edit");
                  handleEditAssetCategory(record);
                }
              }}
              disabled={isEditDisabled}
              style={{
                cursor: isEditDisabled ? "not-allowed" : "pointer",
                opacity: isEditDisabled ? 0.5 : 1,
              }}
              title={!canEdit ? "Permission Denied" : "Edit"}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
            >
              <i className="ti ti-edit" />
            </button>


            <button
              className="me-2"
              onClick={() => {
                if (!isDeleteDisabled) {
                  setSelectedRowData(record);
                  setIsDeleteModalVisible(true);
                }
              }}
              disabled={isDeleteDisabled}
              style={{
                cursor: isDeleteDisabled ? "not-allowed" : "pointer",
                opacity: isDeleteDisabled ? 0.5 : 1,
              }}
              title={!canDelete ? "Permission Denied" : isInactive ? "Inactive Item" : "Delete"}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
            >
              <i className="ti ti-trash" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Asset Category</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="index.html">
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Asset</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Category
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              {canExport && (
                <div className="me-2 mb-2">
                  <div className="dropdown">
                    <Link
                      to="#"
                      className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      <i className="ti ti-file-export me-1" />
                      Export
                    </Link>
                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                      <li>
                        <button
                          onClick={() => exportToCSV(exportAssetCategories, "asset_category")}
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-pdf me-1" />
                          Export as CSV
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => exportToExcel(exportAssetCategories, "asset_category")}
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-xls me-1" />
                          Export as Excel{" "}
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => exportToPDF(exportAssetCategories, "asset_category")}
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-xls me-1" />
                          Export as PDF{" "}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
              {canAdd && (
                <div className="mb-2">
                  <button
                    onClick={() => showModal("create")}
                    className="btn btn-primary d-flex align-items-center"
                  >
                    <i className="ti ti-circle-plus me-2" />
                    Add New Category
                  </button>
                </div>
              )}
              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          {/* Assets Lists */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Asset Category List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-3">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedStatus
                      ? selectedStatus.charAt(0).toUpperCase() +
                      selectedStatus.slice(1)
                      : "Select Status"}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("All")}
                        className="dropdown-item rounded-1"
                      >
                        All
                      </Link>
                    </li>

                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("active")}
                        className="dropdown-item rounded-1"
                      >
                        Active
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("inactive")}
                        className="dropdown-item rounded-1"
                      >
                        Inactive
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedSort
                      ? `Sort By : ${selectedSort}`
                      : "Sort By : Last 7 Days"}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Recently Added")}
                        className="dropdown-item rounded-1"
                      >
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Ascending")}
                        className="dropdown-item rounded-1"
                      >
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Descending")}
                        className="dropdown-item rounded-1"
                      >
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last Month")}
                        className="dropdown-item rounded-1"
                      >
                        Last Month
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last 7 Days")}
                        className="dropdown-item rounded-1"
                      >
                        Last 7 Days
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={columns}
                Selection={true}
              />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Add Assets */}
      <Drawer
        title={modalType === "edit" ? "Edit Category Details" : "Add Category Details"}
        placement="right"
        width={500}
        onClose={handleCancel}
        visible={isModalVisible}
        style={{ marginTop: 50, paddingBottom: 50 }}
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Category Name <span className="text-danger"> *</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={assetCategory.category_name}
                    onChange={(e) => handleInputChange(e, "category_name")}
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3 ">
                  <label className="form-label">
                    Status <span className="text-danger"> *</span>
                  </label>
                  <select
                    className="form-control"
                    value={assetCategory.status}
                    onChange={(e) => handleInputChange(e, "status")}
                  >
                    {status.map((status: any) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit} disabled={loading}
            >{loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
              {modalType === "edit" ? "Update Category" : "Add Category"}
            </button>
          </div>
        </form>
      </Drawer>
      {/* /Add Assets */}
      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.asset_category_id &&
          handleDeleteAssetCategory(selectedRowData.asset_category_id)
        }
      />
    </>
  );
};

export default AssetsCategory;
